<template>
  <div class="yhc-ef-kazai-detail-input">
    <div v-if="initialized">
      <!-- 家財種別s の 表示と選択 (1つ以上ある場合のみ) -->
      <YhcEFKazaiDetailInputSelectKazaiType
        v-if="shouldShowKazaiTypeSelect"
        :currentKazaiId="kazaiData.kazaiTypeId"
        :kazaiTypes="selectableKazaiTypes"
        :userInputName="kazaiData.userInputName"
        :receptionDisabledKazaiTypes="receptionDisabledKazaiTypes"
        :isKaguidoOption="true"
        @selectKazaiType="selectKazaiType"
        @updateUserInputName="updateUserInputName"
      />
      <!-- サイズコンポーネント -->
      <YhcEFKazaiDetailInputSetSizes :isKaguidoOption="true" />
      <!-- 家財ごとオプション選択 -->
      <YhcEFKazaiDetailInputSelectKazaiOptions
        :kazaiType="selectedKazaiType"
        v-if="selectedKazaiType?.serviceOptions?.length"
        :selectedOptionsByServiceId="kazaiData.selectedOptionsByServiceId"
        :originService="originServiceNames.kaguido"
        :isKaguidoFrom="isKaguidoFrom"
        @selectOptions="selectOptions"
      />
      <!-- 移動先の選択 -->
      <YhcEFKazaiKaguidoDetailInputSelectDestination
        :selectedDestinationQuestionAndAnswerByQuestionId="
          kazaiData.selectedDestinationQuestionAndAnswerByQuestionId
        "
        @selectDestinations="selectDestinations"
      />
      <!-- Submit button の表示 -->
      <div class="yhc-ef-kazai-detail-input--submit-area text-center mt-3">
        <button
          :class="isSubmittable ? 'btn-yhc' : 'btn btn-secondary disabled text-white'"
          :disabled="!isSubmittable"
          @click.prevent="addNewKazaiForKaguido"
        >
          <ficon :type="existingKazaiData ? 'edit' : 'plus'" />
          この内容で{{ existingKazaiData ? '更新' : '追加' }}する
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, reactive, ref, computed, onMounted, inject } from 'vue'
import { ModelYhcKazaiTypesFullRelation } from '../../../../fireModels/yhcDataTypes'
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'
import YhcEFKazaiDetailInputSelectKazaiType from '../YhcEFKazaiDetailInput/YhcEFKazaiDetailInputSelectKazaiType.vue'
import YhcEFKazaiDetailInputSetSizes from '../YhcEFKazaiDetailInput/YhcEFKazaiDetailInputSetSizes.vue'
import YhcEFKazaiKaguidoDetailInputSelectDestination from './YhcEFKazaiKaguidoDetailInputSelectDestination.vue'
import YhcEFKazaiDetailInputSelectKazaiOptions from '../YhcEFItemList/YhcEFKazaiDetailInputSelectKazaiOptionsList.vue'
import { YhcEFInputOneKazaiTypeDataObjectForKaguidoOption } from '../../YhcEFInputOneKazaiTypeDataObjectForKaguidoOption'
import { originServiceNames } from '../../formUtils'

export default {
  name: 'YhcEFKazaiKaguidoDetailInput',
  components: {
    YhcEFKazaiDetailInputSelectKazaiOptions,
    YhcEFKazaiDetailInputSetSizes,
    YhcEFKazaiDetailInputSelectKazaiType,
    YhcEFKazaiKaguidoDetailInputSelectDestination,
  },
  props: {
    subCategoryId: {
      type: Number,
      required: true,
    },
    existingKazaiData: {
      type: Object as PropType<YhcEFInputOneKazaiTypeDataObjectForKaguidoOption>,
      default: null,
    },
    isKaguidoFrom: {
      type: Boolean,
    },
  },
  emits: ['addKazai'],
  setup(props, { emit }) {
    const YhcEstimateFormFrontServiceInstance = inject(
      'YhcEstimateFormFrontServiceInstance',
    ) as YhcEstimateFormFrontService
    const initialized = ref(false)
    const kazaiData = ref(
      props.existingKazaiData?.clone() ||
        reactive(
          new YhcEFInputOneKazaiTypeDataObjectForKaguidoOption(
            YhcEstimateFormFrontServiceInstance,
          ),
        ),
    )

    const relatedKazaiTypes = computed(() => {
      return YhcEstimateFormFrontServiceInstance.kazaiTypesWithBaseDatetime.filter(
        (kazaiType) => {
          return kazaiType.subCategory?.id === props.subCategoryId
        },
      )
    })

    const receptionDisabledKazaiTypes = computed(() => {
      return relatedKazaiTypes.value.filter(
        (kazaiType) => kazaiType.receptionDisabled === true,
      )
    })

    const selectableKazaiTypes = computed(() => {
      return relatedKazaiTypes.value.filter((kazaiType) => {
        return kazaiType.receptionDisabled !== true
      })
    })

    const selectedKazaiType = computed(() => {
      return (
        selectableKazaiTypes.value.find(
          (kazaiType) => kazaiType.id === kazaiData.value.kazaiTypeId,
        ) || null
      )
    })

    const isSubmittable = computed(() => {
      return kazaiData.value.isAddable
    })

    const shouldShowKazaiTypeSelect = computed(() => {
      return true // 03.13 一旦 常に表示でも良いかも...
    })

    onMounted(() => {
      if (!kazaiData.value.kazaiTypeId) {
        kazaiData.value.kazaiTypeId = selectableKazaiTypes.value[0]?.id || null
      }
      initialized.value = true
    })

    const selectKazaiType = (_kazaiTypeId) => {
      kazaiData.value.kazaiTypeId = _kazaiTypeId
    }

    const updateUserInputName = (_name) => {
      kazaiData.value.userInputName = _name
    }

    const selectOptions = (_selectedOptionsByServiceId) => {
      kazaiData.value.selectedOptionsByServiceId = _selectedOptionsByServiceId
    }

    const selectDestinations = (_selectedDestinationQuestionAndOptionId) => {
      kazaiData.value.selectedDestinationQuestionAndAnswerByQuestionId =
        _selectedDestinationQuestionAndOptionId
    }

    const addNewKazaiForKaguido = () => {
      if (!kazaiData.value.isAddable) {
        return
      }
      if (props.existingKazaiData) {
        kazaiData.value.id = props.existingKazaiData.id
      }
      emit('addKazai', kazaiData.value)
    }

    return {
      initialized,
      kazaiData,
      relatedKazaiTypes,
      receptionDisabledKazaiTypes,
      selectableKazaiTypes,
      selectedKazaiType,
      isSubmittable,
      shouldShowKazaiTypeSelect,
      selectKazaiType,
      updateUserInputName,
      selectOptions,
      selectDestinations,
      addNewKazaiForKaguido,
      originServiceNames,
    }
  },
}
</script>

<style lang="scss"></style>
